<template>
  <div>
    <PageAllow controller="SubAccountConfigs" action="view" />
    <Breadcrumbs :items="items" />
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{
            $t(isNew ? "CRUD.newInfo" : "CRUD.editInfo", [
              type == 1
                ? $t("SubAccountConfig.types.dishConfigs")
                : type == 2
                ? $t("SubAccountConfig.types.materialConfigs")
                : $t("SubAccountConfig.title"),
            ])
          }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="
                  $permission.checkAccountExpired().then(() => {
                    $router.replace('/material-order-rules?type=' + ruleType);
                  })
                "
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("SubAccountConfig.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>

        <v-card-text>
          <ValidationObserver ref="observer">
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t("SubAccountConfig.DetailFields.name") + $t("Global.colon")
                }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('SubAccountConfig.DetailFields.name')"
                  rules="required"
                >
                  <v-text-field
                    single-line
                    v-model="subAccountConfig.name"
                    :hint="isNew ? '' : $t('SubAccountConfig.nameHint')"
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'name')
                        .concat(errors)
                    "
                  >
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ subAccountConfig.name }}
              </v-col>
            </v-row>
            <v-row align="center" v-if="defaultFlg">
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t("SubAccountConfig.DetailFields.default_flg") +
                  $t("Global.colon")
                }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('SubAccountConfig.DetailFields.default_flg')"
                  rules="required"
                >
                  <v-checkbox
                    class="mt-2 mb-n2"
                    color="primary"
                    v-model="subAccountConfig.default_flg"
                    :value="1"
                    :disabled="defaultFlg == 1 && !isNew"
                    required
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'default_flg')
                        .concat(errors)
                    "
                  >
                    <template v-slot:label>
                      <span class="caption ms-n2">
                        {{
                          subAccountConfig.default_flg
                            ? $t("SubAccountConfig.default_flg.on")
                            : $t("SubAccountConfig.default_flg.off")
                        }}
                      </span>
                    </template>
                  </v-checkbox>
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                <v-chip small label color="primary">
                  {{
                    subAccountConfig.default_flg
                      ? $t("SubAccountConfig.default_flg.on")
                      : $t("SubAccountConfig.default_flg.off")
                  }}
                </v-chip>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t("SubAccountConfig.DetailFields.status") +
                  $t("Global.colon")
                }}
                <Required :show="defaultFlg == 1" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('SubAccountConfig.DetailFields.status')"
                  :rules="defaultFlg == 1 ? 'required' : ''"
                >
                  <v-checkbox
                    class="mt-2 mb-n2"
                    color="primary"
                    v-model="subAccountConfig.status"
                    :value="1"
                    :disabled="defaultFlg == 1 && !isNew"
                    required
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'status')
                        .concat(errors)
                    "
                  >
                    <template v-slot:label>
                      <span class="caption ms-n2">
                        {{
                          subAccountConfig.status
                            ? $t("SubAccountConfig.status.on")
                            : $t("SubAccountConfig.status.off")
                        }}
                      </span>
                    </template>
                  </v-checkbox>
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                <v-chip small label color="primary">
                  {{
                    subAccountConfig.status
                      ? $t("SubAccountConfig.status.on")
                      : $t("SubAccountConfig.status.off")
                  }}
                </v-chip>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t(
                    "SubAccountConfig.DetailFields.sub_account_percent_setting"
                  ) + $t("Global.colon")
                }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7">
                <v-btn
                  v-if="savable"
                  color="primary"
                  class="my-1 mr-3"
                  outlined
                  x-small
                  @click="editSubAccountPercentDialog = true"
                >
                  {{ $t("CRUD.setting", [""]) }}
                </v-btn>
                <br v-if="savable" />
                <!-- 百分比设置 -->
                <v-data-table
                  :headers="percentSettingHeaders"
                  :items="sub_account_percents"
                  multi-sort
                  class="elevation-1 px-4 pt-2"
                  disable-pagination
                  hide-default-footer
                  fixed-header
                >
                  <template v-slot:item.percent="{ item }">
                    {{ item.percent }}{{ $t("Global.rate") }}
                  </template>
                </v-data-table>
              </v-col>
              <!-- <v-col cols="9" md="7" v-else>
                <a @click="editSubAccountPercentDialog = true">
                  {{ $t("CRUD.read") }}
                </a>
              </v-col> -->
            </v-row>
            <v-row v-if="!defaultFlg">
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  (type == 1
                    ? $t("SubAccountConfig.DetailFields.dish_category")
                    : $t("SubAccountConfig.DetailFields.material_category")) +
                  $t("Global.colon")
                }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-card class="mb-4">
                  <v-progress-linear
                    :indeterminate="loading"
                  ></v-progress-linear>
                  <v-btn
                    text
                    x-large
                    color="primary"
                    @click.stop="categoriesOpenAll = !categoriesOpenAll"
                  >
                    {{ $t("openAll") }} / {{ $t("closeAll") }}
                  </v-btn>
                  <v-divider />
                  <div
                    style="max-height: 304px"
                    class="overflow-y-auto overflow-x-hidden"
                  >
                    <v-sheet>
                      <v-treeview
                        class="py-2"
                        v-model="categoriesTree"
                        :items="categories"
                        :search="categoriesSearch"
                        :filter="categoriesFilter"
                        item-key="id"
                        item-children="categories"
                        open-all
                        hoverable
                        activatable
                        return-object
                        :open.sync="categoriesOpen"
                        selectable
                        selected-color="primary"
                        selection-type="independent"
                      >
                        <template v-slot:label="{ item }">
                          <v-chip>
                            {{
                              type == 1
                                ? item.code
                                : type == 2
                                ? item.number
                                : ""
                            }}
                          </v-chip>
                          {{ item.name }}
                        </template>
                      </v-treeview>
                    </v-sheet>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="9" md="7" v-else>
                <v-chip
                  v-for="(c, index) in categoriesTree"
                  :key="index"
                  class="mr-1 mt-n1 mb-3"
                  color="primary"
                  outlined
                >
                  {{
                    type == 1
                      ? c.code + " / " + c.name
                      : type == 2
                      ? c.number + " / " + c.name
                      : ""
                  }}
                </v-chip>
              </v-col>
            </v-row>
            <v-row v-if="!defaultFlg">
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t("SubAccountConfig.DetailFields.shop_district", [
                    $consts.shopName(),
                  ]) + $t("Global.colon")
                }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-card class="mb-4">
                  <v-progress-linear
                    :indeterminate="loading"
                  ></v-progress-linear>
                  <v-btn
                    text
                    x-large
                    color="primary"
                    @click.stop="districtsOpenAll = !districtsOpenAll"
                  >
                    {{ $t("openAll") }} / {{ $t("closeAll") }}
                  </v-btn>
                  <v-divider />
                  <div
                    style="max-height: 304px"
                    class="overflow-y-auto overflow-x-hidden"
                  >
                    <v-sheet>
                      <v-treeview
                        class="py-2"
                        v-model="districtsTree"
                        :items="districts"
                        :search="districtsSearch"
                        :filter="districtsFilter"
                        item-key="code"
                        item-children="districts"
                        :open="districtsOpen"
                        hoverable
                        activatable
                        return-object
                        selectable
                        selected-color="primary"
                        selection-type="independent"
                      >
                        <template v-slot:label="{ item }">
                          <v-chip> {{ item.number }} </v-chip>
                          {{ item.name }}
                        </template>
                      </v-treeview>
                    </v-sheet>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="9" md="7" v-else>
                <v-chip
                  v-for="(d, index) in districtsTree"
                  :key="index"
                  class="mr-1 mt-n1 mb-3"
                  color="primary"
                  outlined
                >
                  {{ d.number + " / " + d.name }}
                </v-chip>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t("SubAccountConfig.DetailFields.memo") + $t("Global.colon")
                }}
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-textarea
                  v-model="subAccountConfig.memo"
                  outlined
                  auto-grow
                />
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ subAccountConfig.memo }}
              </v-col>
            </v-row>
          </ValidationObserver>
        </v-card-text>
      </v-card>
      <v-sheet class="page-opts">
        <v-card>
          <v-card-title>
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  outlined
                  color="secondary"
                  class="elevation-1"
                  block
                  @click="
                    $permission.checkAccountExpired().then(() => {
                      $router.replace(
                        '/settings/sub-account-settings/sub-account-configs?type=' +
                          type
                      );
                    })
                  "
                >
                  <v-icon small class="pe-1">fa-times</v-icon>
                  {{
                    $t("CRUD.backToList", [$t("SubAccountConfig.ObjectName")])
                  }}
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                v-if="
                  isView &&
                  $permission.permissionAllow('SubAccountConfigs', 'edit')
                "
              >
                <v-btn
                  color="accent"
                  block
                  :disabled="loading && !subAccountConfig.id"
                  @click="
                    $permission.checkAccountExpired().then(() => {
                      $router.push({
                        path:
                          '/settings/sub-account-settings/sub-account-configs/' +
                          subAccountConfig.id,
                        query: { type: type, mode: 'edit' },
                      });
                    });
                    mode = 'edit';
                  "
                >
                  <v-icon small class="pe-1">fa-pencil-alt</v-icon>
                  {{ $t("CRUD.editInfo", [$t("SubAccountConfig.ObjectName")]) }}
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                v-if="
                  savable &&
                  $permission.permissionAllow(
                    'SubAccountConfigs',
                    isNew ? 'add' : 'edit'
                  )
                "
              >
                <v-btn
                  color="success"
                  block
                  :disabled="loading && !subAccountConfig.id"
                  @click="submit()"
                >
                  <v-icon small class="pe-1">fa-save</v-icon>
                  {{ $t("CRUD.saveInfo", [$t("SubAccountConfig.ObjectName")]) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>
      </v-sheet>
    </v-sheet>

    <!-- 分账百分比设置 -->
    <v-dialog v-model="editSubAccountPercentDialog" width="600px">
      <v-card>
        <v-card-title class="mx-3">
          {{ $t("SubAccountConfig.subAccountPercentSettings.title") }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="editSubAccountPercentDialog = false"
              >
                fa-times
              </v-icon>
            </template>
            <span>{{ $t("$vuetify.close") }}</span>
          </v-tooltip>
        </v-card-title>

        <ValidationObserver ref="sub-account-percents-observer">
          <!-- {{sub_account_percents}} -->
          <v-data-table
            :headers="percentSettingHeaders"
            :items="sub_account_percents"
            :loading="loading"
            multi-sort
            disable-pagination
            hide-default-footer
            class="elevation-0 px-6"
            height="400px"
            fixed-header
          >
            <template v-slot:item.percent="{ item }">
              <span v-if="isView">
                {{ item.percent }}{{ $t("Global.rate") }}
              </span>
              <v-text-field
                v-else
                single-line
                dense
                hide-details
                v-model="item.percent"
                type="number"
                min="0"
                :suffix="$t('Global.rate')"
                @blur="
                  item.percent = $numberFormatter.formatWithDotNoMoreThan(
                    item.percent,
                    2
                  )
                "
                style="max-width: 100px; margin-left: auto"
              />
            </template>
          </v-data-table>
        </ValidationObserver>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            outlined
            @click="editSubAccountPercentDialog = false"
          >
            {{ $t("$vuetify.close") }}
          </v-btn>
          <v-btn
            v-if="savable"
            color="accent"
            @click="editSubAccountPercentDialog = false"
          >
            {{ $t("CRUD.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Required from "@/components/plugins/Required";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import SubAccountConfigs from "@/api/sub_account_configs";
import SubAccountSettings from "@/api/sub_account_settings";
import DishCategories from "@/api/dish_categories";
import MaterialCategories from "@/api/material_categories";
import Districts from "@/api/districts";

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    Required,
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("SettingNav.split"),
          disabled: false,
          href: "/settings/sub-account-settings/dish-sub-account-configs",
        },
        {
          text: i18n.t("SettingNav.subAccountConfig"),
          disabled: true,
          href: "/settings/sub-account-settings/dish-sub-account-configs",
        },
      ];
    },
    isNew() {
      return this.mode == "new";
    },
    type() {
      return this.$route.query.type;
    },
    savable() {
      return this.mode == "edit" || this.mode == "new";
    },
    isView() {
      return this.mode == "view";
    },
    percentSettingHeaders() {
      return [
        {
          text: i18n.t(
            "SubAccountConfig.subAccountPercentSettings.sub_account_type"
          ),
          align: "center",
          sortable: true,
          value: "name",
        },
        {
          text: i18n.t(
            "SubAccountConfig.subAccountPercentSettings.sub_account_percent"
          ),
          align: "end",
          sortable: true,
          value: "percent",
        },
      ];
    },
    categoriesFilter() {
      return (item, search) =>
        item.name.indexOf(search) > -1 || item.code.indexOf(search) > -1;
    },
    districtsFilter() {
      return (item, search) =>
        item.name.indexOf(search) > -1 || item.number.indexOf(search) > -1;
    },

    // categoriesTree() {
    //   if (this.subAccountConfig.categories) {
    //     this.subAccountConfig.categories.map((c) => {
    //       this.findCategorySelected(this.categories, c);
    //       if (this.categorySelected) {
    //         this.categoriesTree.push(this.categorySelected);
    //         this.categorySelected = null;
    //       }
    //     });
    //   }
    // },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    mode: "",
    subAccountConfig: {},
    defaultFlg: 0,
    serverErrors: [],
    editSubAccountPercentDialog: false,
    subAccountRequstFlg: false,
    sub_account_percents: [],
    categoyRequstFlg: false,
    categoriesTree: [],
    categorySelected: null, //记录选中分类item
    categories: [],
    categoriesSearch: "",
    categoriesOpenAll: true,
    categoriesOpen: [],
    districtRequestFlg: false,
    districtsTree: [],
    districtSelected: null, //记录选中分组item
    districts: [],
    districtsSearch: "",
    districtsOpenAll: true,
    districtsOpen: [],
  }),

  mounted() {
    if (this.$route.params.id == 0) {
      this.mode = "new";
      this.defaultFlg = this.$route.query.mode == "defaultNew" ? 1 : 0;
    } else {
      this.mode = this.$route.query.mode ? this.$route.query.mode : "view";
    }
    //获取分账
    SubAccountSettings.fetchAll().then(
      function (response) {
        if (response.data.status.isOK()) {
          if (response.data.sub_account_settings) {
            for (let key in response.data.sub_account_settings) {
              this.sub_account_percents.push(
                Object.assign({}, response.data.sub_account_settings[key], {
                  percent: "",
                })
              );
            }
          }
          this.subAccountRequstFlg = true; //标记接口请求成功
        }
      }.bind(this)
    );
    //获取 菜品/商品 分类
    if (this.$route.query.type == 1) {
      DishCategories.tree().then(
        function (response) {
          if (response.data.status.isOK()) {
            this.categories = response.data.categories;
            this.categoriesOpenTree(this.categories);
            this.categoyRequstFlg = true; //标记接口请求成功
          }
        }.bind(this)
      );
    } else if (this.$route.query.type == 2) {
      MaterialCategories.tree().then(
        function (response) {
          if (response.data.status.isOK()) {
            this.categories = response.data.categories;
            this.categoriesOpenTree(this.categories);
            this.categoyRequstFlg = true; //标记接口请求成功
          }
        }.bind(this)
      );
    }

    // 获取门店分组
    Districts.tree().then(
      function (response) {
        if (response.data.status.isOK()) {
          this.districts = response.data.districts;

          this.districtsOpenTree(this.districts);
          this.districtRequestFlg = true; // 标记接口请求成功
        }
      }.bind(this)
    );
  },

  methods: {
    getSubAccountConfigFromAPI() {
      SubAccountConfigs.fetchOne(this.$route.params.id).then(
        function (response) {
          if (response.data.status) {
            this.subAccountConfig = response.data.sub_account_config;
            this.defaultFlg = response.data.sub_account_config.default_flg;

            var conditions = response.data.sub_account_config.conditions;
            if (
              conditions &&
              Object.keys(conditions.sub_account_percent_json).length
            ) {
              this.sub_account_percents = this.sub_account_percents.map((s) => {
                if (conditions.sub_account_percent_json[s.name]) {
                  s.percent =
                    conditions.sub_account_percent_json[s.name].percent;
                }
                return s;
              });
            }

            if (this.subAccountConfig.categories && this.categories.length) {
              this.subAccountConfig.categories.map((c) => {
                this.findCategorySelected(this.categories, c);
                if (this.categorySelected) {
                  this.categoriesTree.push(this.categorySelected);
                  this.categorySelected = null;
                }
              });
            }

            if (this.subAccountConfig.districts) {
              this.subAccountConfig.districts.map((d) => {
                this.findDistrictSelected(this.districts, d);
                if (this.districtSelected) {
                  this.districtsTree.push(this.districtSelected);
                  this.districtSelected = null;
                }
              });
            }
          }
        }.bind(this)
      );
    },
    categoriesOpenTree(openArr) {
      for (var i = 0; i < openArr.length; i++) {
        this.categoriesOpen.push(openArr[i]);
        if (openArr[i].categories.length > 0) {
          this.categoriesOpenTree(openArr[i].categories);
        }
      }
    },
    districtsOpenTree(openArr) {
      for (var i = 0; i < openArr.length; i++) {
        this.districtsOpen.push(openArr[i]);
        if (openArr[i].districts.length > 0) {
          this.districtsOpenTree(openArr[i].districts);
        }
      }
    },
    submit() {
      this.$store.dispatch("fullscreenLoading");
      this.serverErrors = []; //重置远端错误提示
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            var subAccountConfig = JSON.parse(
              JSON.stringify(this.subAccountConfig)
            );
            if (!subAccountConfig.status) {
              subAccountConfig.status = 0;
            }
            if (!subAccountConfig.default_flg) {
              subAccountConfig.default_flg = 0;
            }
            subAccountConfig.conditions.sub_account_percent_json = {};
            this.sub_account_percents.forEach((p) => {
              if (p.percent > 0) {
                subAccountConfig.conditions.sub_account_percent_json[p.name] = {
                  percent: p.percent,
                };
              }
            });
            if (this.type == 1) {
              subAccountConfig.categories = this.categoriesTree.map((c) => {
                return c.id;
              });
            } else if (this.type == 2) {
              subAccountConfig.categories = this.categoriesTree.map((c) => {
                return c.code;
              });
            }

            subAccountConfig.districts = this.districtsTree.map((c) => {
              return c.code;
            });

            SubAccountConfigs.save(this.subAccountConfig.id, subAccountConfig)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("mode", null);
                    this.$emit("input", null);
                    this.$router.push({
                      path: "/settings/sub-account-settings/sub-account-configs",
                      query: {
                        type: this.type,
                      },
                    });
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              )
              .catch(
                function () {
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
    findCategorySelected(categories, key) {
      categories.some((category) => {
        let category_key =
          this.type == 1 ? category.id : this.type == 2 ? category.code : "";
        if (category_key) {
          if (category_key == key) {
            this.categorySelected = category; //用全局变量赋值，局部变量赋值有误差
            return true;
          } else if (category.categories.length) {
            this.findCategorySelected(category.categories, key);
          }
        }
      });
    },
    findDistrictSelected(districts, code) {
      districts.some((d) => {
        if (d.code == code) {
          this.districtSelected = d; //用全局变量赋值，局部变量赋值有误差
          return true;
        } else if (d.districts.length) {
          this.findDistrictSelected(d.districts, code);
        }
      });
    },
  },

  watch: {
    mode: {
      handler() {
        if (this.isNew) {
          this.subAccountConfig.type = Number(this.type);
          this.subAccountConfig.default_flg = this.defaultFlg ? 1 : 0;
          this.subAccountConfig.status = 1;
          this.subAccountConfig.conditions = { sub_account_percent_json: {} };
        }
        // else if (this.$route.params.id
        // ) {
        //   this.getSubAccountConfigFromAPI();
        // }
      },
    },
    subAccountRequstFlg: {
      handler() {
        if (
          this.subAccountRequstFlg &&
          this.categoyRequstFlg &&
          this.districtRequestFlg &&
          this.$route.params.id &&
          !this.isNew
        ) {
          this.getSubAccountConfigFromAPI();
        }
      },
    },
    categoyRequstFlg: {
      handler() {
        if (
          this.subAccountRequstFlg &&
          this.categoyRequstFlg &&
          this.districtRequestFlg &&
          this.$route.params.id &&
          !this.isNew
        ) {
          this.getSubAccountConfigFromAPI();
        }
      },
    },
    districtRequestFlg: {
      handler() {
        if (
          this.subAccountRequstFlg &&
          this.categoyRequstFlg &&
          this.districtRequestFlg &&
          this.$route.params.id &&
          !this.isNew
        ) {
          this.getSubAccountConfigFromAPI();
        }
      },
    },
    categoriesOpenAll: {
      handler() {
        if (this.categoriesOpenAll) {
          this.categoriesOpenTree(this.categories);
        } else {
          this.categoriesOpen = [];
        }
      },
    },
    districtsOpenAll: {
      handler() {
        if (this.districtsOpenAll) {
          this.districtsOpenTree(this.districts);
        } else {
          this.districtsOpen = [];
        }
      },
    },
    // subAccountConfig: {
    //   deep: true,
    //   handler() {
    // if (this.subAccountConfig.categories) {
    // console.log("ok")
    //   this.subAccountConfig.categories.map((c) => {
    //     console.log("cc: ",c)
    //     this.findCategorySelected(this.categories, c);
    //     if (this.categorySelected) {
    //       this.categoriesTree.push(this.categorySelected);
    //       this.categorySelected = null;
    //     }
    //   });
    // }

    // if (this.subAccountConfig.districts) {
    //   this.subAccountConfig.districts.map((d) => {
    //     this.findDistrictSelected(this.districts, d);
    //     if (this.districtSelected) {
    //       this.districtsTree.push(this.districtSelected);
    //       this.districtSelected = null;
    //     }
    //   });
    // }
    //   },
    // },
  },
};
</script>